import {WebElement} from './abstract-element.js';
import {html, unsafeHTML} from '../libs/lit.dist.js';
import {doSearchFilter, doSearchItemFilter, indexBy} from '../util/collections.js';
import {cancelDomEvent} from "../util/browser.js";

const DIR_UP = -1;
const DIR_DOWN = 1;
let nextId = 1;

/**
 *
 * Atrybuty:
 *  {object[]} options    - Opcje do wyświetlenie (tablica JS lub zakodowane w JSON)
 *  {string[]} value      - Wybrane wartości opcji (tablica JS lub zakodowane w JSON)
 *  {bool}     searchable - Czy pozwalać na wyszukiwanie (domyślnie wyłączone)
 *  {bool}     sortable   - Czy pozwalać zmianę kolejności w prawym panelu (domyślnie włączone, "0'" lub "false" aby wyłączyć)
 *  {function} onChange   - Callback po zmianie zawartości prawego panelu (tylko w JS)
 *  {bool}     disabled   - Pole wyłączone (działa jak atrybut disabled w HTML)
 *  {string}   name       - Nazwa w formularzu (przy wysyłaniu formularza)
 *  {string}   labelLeft  - Etykieta lewego panelu
 *  {string}   labelRight - Etykieta prawego panelu
 *  {string}   labels     - Nazwa pola zawierającego etykietę dla opla <option> w obiekcie z tablicy z atrybutu options, domyślnie "label"
 *  {string}   values     - Nazwa pola zawierającego wartość dla opla <option> w obiekcie z tablicy z atrybutu options, domyślnie "value"
 *  {string}   isDisabled - Nazwa pola zawierającego flagę disabled dla opla <option> w obiekcie z tablicy z atrybutu options, domyślnie "disabled"
 *
 *  Zdarzenia:
 *   change - zdarzenie po zmianie zawartości prawego panelu
 *
 * Przykład użycia w HTML:
 *  <pk-linked-lists
 *   labelLeft="Dostępne cenniki"
 *   labelRight="Cenniki wybrane do przypisania"
 *   options='[{"id": 1, "name": "foo", ...}]' <!-- [!!] Ważne aby użyć ' ponieważ " jest używany w JSON -->
 *   value='[1, ...]'
 *   name="prices_list[]"
 *   labels="name"
 *   values="id"
 *   sortable="0"
 *   searchable
 *   allowAll
 *  ></pk-linked-lists>
 *
 * Przykład użycia w LIT:
 *  <pk-linked-lists
 *   labelLeft="Dostępne cenniki"
 *   labelRight="Cenniki wybrane do przypisania"
 *   .options="${[{"id": 1, "name": "foo", ...}]}"
 *   .value="${[1, ...]}"
 *   @change=${e => setValue(e.target.value)}
 *   labels="name"
 *   values="id"
 *   sortable="0"
 *   searchable
 *   allowAll
 *  ></pk-linked-lists>
 *
 */
export class PkLinkedLists extends WebElement {

    static get properties() {
        return {
            options: {type: Array},
            value: {type: Array},
            disabled: {type: Boolean},
            allowAll: {type: Boolean},
            searchable: {type: Boolean},
            searchQueryLeft: {type: String},
            searchQueryRight: {type: String},
            btnClass: {type: String},
            sortable: {},
            onChange: {},
            onSort: {},
            labels: {},
            values: {},
            isDisabled: {type: String},
            name: {type: String},
            labelLeft: {type: String},
            labelRight: {type: String},
            selected: {type: Array},
        };
    }

    set value(value) {
        this.__value = _.map(value, v => String(v));
        this.requestUpdate();
    }

    get value() {
        return this.__value;
    }

    constructor() {
        super();
        this.cid = nextId++;
        this.value = [];
        this.options = [];
        this.disabled = false;
        this.allowAll = false;
        this.sortable = true;
        this.searchable = false;
        this.searchQueryLeft = '';
        this.searchQueryRight = '';
        this.onChange = _.noop;
        this.onSort = _.noop;
        this.name = '';
        this.labels = 'label';
        this.values = 'value';
        this.isDisabled = 'disabled';
        this.labelLeft = '';
        this.labelRight = '';
        this.selected = [];
        this.btnClass = 'btn btn-sm btn-default';

        // Set default style
        this.style.height = '250px';
        this.style.display = 'block';
    }

    isInRightPanel(id) {
        return _.includes(this.value, String(id));
    }

    isSelected(id) {
        return _.includes(this.selected, String(id));
    }

    getSelected() {
        return _.filter(this.getOptions(), m => _.includes(this.selected, m.value));
    }

    getOptions() {
        return this.options.map(model => ({
            label: String(this.getOptionField(model, this.labels) || ''),
            value: String(this.getOptionField(model, this.values) || ''),
            disabled: Boolean(this.getOptionField(model, this.isDisabled) || false),
            model,
        }));
    }

    getValue() {
        const options = indexBy(this.getOptions(), 'value');
        return this.value.map(value => options[value] && options[value].model);
    }

    moveSelected(dir) {
        const nextValue = [...this.value];
        let toMove = [...this.selected];
        let nextIndex, value;

        const maxAllowedIndex = nextValue.length - 1;
        const from = dir === DIR_DOWN ? nextValue.length - 1 : 0;
        const to = dir === DIR_DOWN ? 0 : nextValue.length;
        const step = dir === DIR_DOWN ? -1 : 1;

        for (let i = from; ((step === 1 && i < to) || step === -1 && i >= 0); i += step) {
            value = nextValue[i];
            if (_.includes(toMove, value)) {
                nextIndex = Math.max(i + dir, 0);
                if (nextIndex <= maxAllowedIndex) {
                    nextValue[i] = nextValue[nextIndex];
                    nextValue[nextIndex] = value;
                }
                toMove = _.without(toMove, value);
            }
        }
        this.value = nextValue;
    };

    getFilteredSortedOptions() {
        return this.getOptions();
    }

    getOptionField(option, getter) {
        if (getter && _.isFunction(getter)) {
            return getter(option);
        }
        return option[getter];
    }

    moveSelectedLeft() {
        if (this.selected.length > 0) {
            this.value = _.filter(this.value, value => !_.includes(this.selected, value));
        }
        this.triggerChange();
    }

    moveSelectedRight() {
        this.value = _.filter([
            ...this.value,
            ...this.selected,
        ], value => value !== -1);
        this.triggerChange();
    }

    moveSelectedUp() {
        this.moveSelected(DIR_UP);
        this.triggerChange();
    }

    moveSelectedDown() {
        this.moveSelected(DIR_DOWN);
        this.triggerChange();
    }

    moveAllRight() {
        const options = this.getOptions();
        this.value = doSearchFilter(options, this.searchQueryLeft, m => m.label).map(m => m.value);
    }

    moveAllLeft() {
        const selectedOptionsValues = doSearchFilter(this.getOptions(), this.searchQueryRight, m => m.label).map(m => m.value)
        this.value = this.value.filter(value => !_.includes(selectedOptionsValues, value));
    }

    onChangePanelSelect(event) {
        event.stopPropagation();
        event.preventDefault();
        this.selected = _.map(event.target.selectedOptions, m => m.value);
    }

    triggerChange() {
        const data = {
            options: this.getValue(),
            value: this.value,
        };
        this.dispatchEvent(new CustomEvent('change', {
            detail: data,
            bubbles: true,
        }));
        if (_.isFunction(this.onChange)) {
            this.onChange(data);
        }
    }

    isSortable() {
        return this.sortable !== '0' &&
            this.sortable !== 0 &&
            this.sortable !== 'false' &&
            this.sortable !== false;
    }

    render() {
        const options = this.getFilteredSortedOptions();
        let leftOptions = [];
        let rightOptions = [];
        let isSelectedLeft = false;
        let isSelectedRight = false;

        const getSearchItemContext = m => m.label;
        let leftOptionsCount = 0
        let leftOptionsCountTotal = 0
        let rightOptionsCount = 0
        let rightOptionsCountTotal = 0

        for (let option of options) {
            if (this.isInRightPanel(option.value)) {
                rightOptionsCountTotal++;
                if (this.searchQueryRight.length === 0 || doSearchItemFilter(option, this.searchQueryRight, getSearchItemContext)) {
                    rightOptionsCount++;
                    rightOptions[this.value.indexOf(option.value)] = option;
                    if (this.isSelected(option.value)) {
                        isSelectedRight = true;
                    }
                }
            } else {
                leftOptionsCountTotal++;
                if (this.searchQueryLeft.length === 0 || doSearchItemFilter(option, this.searchQueryLeft, getSearchItemContext)) {
                    leftOptionsCount++;
                    leftOptions.push(option);
                    if (this.isSelected(option.value)) {
                        isSelectedLeft = true;
                    }
                }
            }
        }

        let filteredOutLeftCount = leftOptionsCountTotal - leftOptionsCount;
        let filteredOutRightCount = rightOptionsCountTotal - rightOptionsCount;

        const canMoveUp = isSelectedRight && this.searchQueryRight.length === 0;
        const canMoveDown = isSelectedRight && this.searchQueryRight.length === 0;
        return html`
            <div class="d-flex justify-content-center h-100">
                <div class="w-50 d-flex flex-column left-column">
                    <label for="pk-ll-${this.cid}-col-left" class="fw-700 mb-2" >${unsafeHTML(this.labelLeft)}</label>
                    ${this.searchable ? html`
                        <div class="input-group">
                            <div class="input-group-prepend">
                                <div class="input-group-text border-bottom-left-radius-0 border-bottom-0">
                                    <i class="fa fa-search"></i>
                                </div>
                            </div>
                            <input type="search" .value="${this.searchQueryLeft}" @input="${cancelDomEvent(e => this.searchQueryLeft = e.target.value)}" class="form-control border-bottom-right-radius-0 border-bottom-0" placeholder="Szukaj" aria-label="Szukaj" />
                        </div>
                    ` : ''}
                    <select id="pk-ll-${this.cid}-col-left" size="2" class="form-control left-column-items h-100 ${this.searchable ? 'border-top-left-radius-0 border-top-right-radius-0' : ''}" @change="${e => this.onChangePanelSelect(e)}" .disabled="${this.disabled}" multiple>
                        ${leftOptions.map(m => html`
                            <option .value="${m.value}" .selected="${this.isSelected(m.value)}" .disabled="${m.disabled}" @dblclick="${() => this.moveSelectedRight()}">
                                ${m.label}
                            </option>
                        `)}
                        ${this.searchQueryLeft.length > 0 && filteredOutLeftCount > 0 ? html`
                            <option disabled>Ukryto ${filteredOutLeftCount} pozycji</option>
                        ` : ''}
                    </select>
                </div>
                <div class="width-4 px-2 pt-5">
                    <div class="btn-group-vertical d-flex">
                        <button type="button" class="${this.btnClass}" title="Przenieś w prawo" @click=${() => this.moveSelectedRight()} .disabled="${this.disabled || !isSelectedLeft}">
                            <i class="fas fa-chevron-right"></i>
                        </button>
                        <button type="button" class="${this.btnClass}" title="Przenieś w lewo" @click="${() => this.moveSelectedLeft()}" .disabled="${this.disabled || !isSelectedRight}">
                            <i class="fas fa-chevron-left"></i>
                        </button>
                        ${this.allowAll ? html`
                            <button type="button" class="${this.btnClass}" title="Przenieś wszystkie widoczne w prawo" @click=${() => this.moveAllRight()} .disabled="${this.disabled || leftOptionsCount === 0}">
                                <i class="fas fa-chevron-double-right"></i>
                            </button>
                            <button type="button" class="${this.btnClass}" title="Przenieś wszystkie widoczne w left" @click="${() => this.moveAllLeft()}" .disabled="${this.disabled || rightOptionsCount === 0}">
                                <i class="fas fa-chevron-double-left"></i>
                            </button>
                        ` : ''}
                        ${this.isSortable() ? html`
                            <button type="button" class="${this.btnClass}" title="Przesuń w górę" @click="${() => this.moveSelectedUp()}" .disabled="${this.disabled || !canMoveUp}">
                                <i class="fas fa-chevron-up"></i>
                            </button>
                            <button type="button" class="${this.btnClass}" title="Przesuń w dół" @click="${() => this.moveSelectedDown()}" .disabled="${this.disabled || !canMoveDown}">
                                <i class="fas fa-chevron-down"></i>
                            </button>
                        ` : ''}
                    </div>
                </div>
                <div class="w-50 d-flex flex-column right-column">
                    <label for="pk-ll-${this.cid}-col-right" class="fw-700 mb-2">${unsafeHTML(this.labelRight)}</label>
                    ${this.searchable ? html`
                        <div class="input-group">
                            <div class="input-group-prepend">
                                <div class="input-group-text border-bottom-left-radius-0 border-bottom-0">
                                    <i class="fa fa-search"></i>
                                </div>
                            </div>
                            <input type="search" .value="${this.searchQueryRight}" @input="${cancelDomEvent(e => this.searchQueryRight = e.target.value)}" class="form-control border-bottom-right-radius-0 border-bottom-0" placeholder="Szukaj" aria-label="Szukaj" />
                        </div>
                    ` : ''}
                    <select id="pk-ll-${this.cid}-col-right" size="2" class="form-control right-column-items h-100 ${this.searchable ? 'border-top-left-radius-0 border-top-right-radius-0' : ''}" @change="${e => this.onChangePanelSelect(e)}" .disabled="${this.disabled}" multiple>
                        ${rightOptions.map(m => html`
                            <option .value="${m.value}" .selected="${this.isSelected(m.value)}" .disabled="${m.disabled}" @dblclick="${() => this.moveSelectedLeft()}">
                                ${m.label}
                            </option>
                        `)}
                        ${this.searchQueryRight.length > 0 && filteredOutRightCount > 0 ? html`
                            <option disabled>Ukryto ${filteredOutRightCount} pozycji</option>
                        ` : ''}
                    </select>
                </div>
            </div>
            ${this.name ? this.value.map(value => html`
                <input type="hidden" name="${this.name}" .value="${value}"/>
            `) : ''}
        `;
    }
}

window.customElements.define('pk-linked-lists', PkLinkedLists);
